import { ref, onValue, update } from "firebase/database";
import { YEAR } from "./config";
import { planInsertShift } from "./spots";

// Spot assignments live at spotAssignments2026/{date}/{spot} as
// { key, bookedBy, alias }. `key` ties the spot back to a pool entry
// (the booker's uid); `alias` is the guest actually sitting there and
// is kept when a spot is unassigned, so relabelling survives a redo.
export const SPOT_ASSIGNMENTS_PATH = `spotAssignments${YEAR}`;

const spotRef = (db, date, spot) =>
  ref(db, `${SPOT_ASSIGNMENTS_PATH}/${date}/${spot}`);

// Same resubscribe-on-cancel behaviour as the availability counters:
// a listener denied by security rules is dead permanently otherwise.
export const watchSpotAssignments = (db, callback) => {
  let stopped = false;
  let unsubscribe = null;
  let timer = null;
  let attempt = 0;

  const subscribe = () => {
    if (stopped) return;
    try {
      unsubscribe = onValue(
        ref(db, SPOT_ASSIGNMENTS_PATH),
        (snapshot) => {
          attempt = 0;
          callback(snapshot.val() || {});
        },
        () => {
          unsubscribe = null;
          callback(null);
          const delay = Math.min(60000, 5000 * 2 ** attempt);
          attempt += 1;
          timer = setTimeout(subscribe, delay);
        },
      );
    } catch (error) {
      callback(null);
    }
  };

  subscribe();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    if (unsubscribe) unsubscribe();
  };
};

export const assignSpot = (db, date, spot, { key, bookedBy }) =>
  update(spotRef(db, date, spot), { key, bookedBy });

// Firebase drops null members, so this doubles as "clear what isn't set".
const seatValue = (assignment) => ({
  key: assignment.key || null,
  bookedBy: assignment.bookedBy || null,
  alias: assignment.alias || null,
});

// Rearranging: drop a set on another spot to insert it there, sliding
// the sets in between one place over to make room. Guest names travel
// with their sets — an alias names the person, not the patch of sand.
// Written as one multi-path update so a shift can never half-apply and
// leave two people on one spot.
export const moveSpot = (db, date, fromSpot, toSpot, dayAssignments) => {
  const plan = planInsertShift(dayAssignments, fromSpot, toSpot);
  const spots = Object.keys(plan);
  if (!spots.length) return Promise.resolve();

  const updates = {};
  spots.forEach((spot) => {
    updates[`${SPOT_ASSIGNMENTS_PATH}/${date}/${spot}`] = plan[spot]
      ? seatValue(plan[spot])
      : null;
  });
  return update(ref(db), updates);
};

// Clears the booking but deliberately leaves any alias in place.
export const unassignSpot = (db, date, spot) =>
  update(spotRef(db, date, spot), { key: null, bookedBy: null });

export const setSpotAlias = (db, date, spot, alias) =>
  update(spotRef(db, date, spot), { alias: alias?.trim() || null });
